import { useAccess } from '../../lib/access/access.context';
import type { Permission } from '../../lib/permissions';
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-900',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-90',
        // A quieter destructive affordance for a repeated per-row trigger
        // (e.g. "Deactivate" in a list) -- the pastel -bg token, not an
        // opacity modifier against bg-destructive (that syntax doesn't
        // reliably resolve against a CSS-custom-property color, a real bug
        // found elsewhere in this app). Solidifies to the full destructive
        // color on hover, so the real warning still shows on interaction --
        // the actual point of commitment stays the confirm dialog's own
        // solid destructive button, never this trigger.
        destructiveSoft:
          'bg-destructive-bg text-destructive hover:bg-destructive hover:text-destructive-foreground',
        outline: 'border border-input bg-card hover:bg-muted hover:text-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-primary-100',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  permission?: Permission;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, permission, ...props }, ref) => {
    const access = useAccess();
    if (permission && !access.allows(permission)) return null;
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button };
